import React, { useEffect } from "react";
import { getFirestore, doc, deleteDoc, updateDoc } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import { auth } from "../firebase";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/navbar";
import data from "../data/data.json"; // JSON file with data

const db = getFirestore();

const MyAccount = () => {
    const navigate = useNavigate();
    const { user, userData, isLoggedIn, logout, loading, deleteUser } = useAuth();

    useEffect(() => {
        // Redirect to login if not logged in
        if (loading) {
            // Show loading spinner or placeholder
            return;
        }
        else if (!isLoggedIn) {
            navigate("/login");
        }
        return () => {
            // Cleanup if needed
        };
    }, [isLoggedIn, loading, navigate]);

    const {
        displayName,
        photoURL,
        activationCode,
        activatedAt,
        language,
        accountType, // free, pro, premium, unlimited
        imType, // whatsapp, telegram
        imState, // active, inactive
    } = userData || {};

    const whatsappLink = `${data.whatsappUrl}?text=join%20${activationCode}`;

    const Activate = () => {

        return (
            <div className="w-full h-full text-center flex flex-col items-center justify-around">
                <div>
                    <h1 className="text-pf-titleDark leading-normal pt-6">
                        {data.myAccount.inactive.title}
                    </h1>
                    <p className="mb-6 text-gray-600">
                        {data.myAccount.inactive.subtitle}
                    </p>
                </div>
                <a
                    href={whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full md:w-80 bg-green-600 hover:bg-green-700 text-gray-50 hover:text-white px-6 py-3 rounded-lg no-underline text-lg font-semibold shadow transition flex items-center justify-center"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 32 32"
                        fill="currentColor"
                        className="w-5 h-5 mr-2"
                    >
                        <path d="M16 0C7.163 0 0 7.163 0 16c0 2.837.74 5.566 2.14 7.98L0 32l8.293-2.12A15.92 15.92 0 0016 32c8.837 0 16-7.163 16-16S24.837 0 16 0zm7.68 22.24c-.32.9-1.86 1.74-2.56 1.86-.7.12-1.54.16-2.48-.16-.58-.18-1.32-.42-2.26-.82-3.98-1.72-6.56-5.96-6.76-6.24-.2-.28-1.6-2.12-1.6-4.04 0-1.92 1-2.86 1.4-3.26.36-.36.8-.44 1.06-.44h.76c.24 0 .56-.08.88.68.32.76 1.12 2.64 1.22 2.84.1.2.16.44.04.72-.12.28-.18.44-.36.68-.2.24-.36.4-.56.64-.2.24-.4.48-.18.88.24.4 1.06 1.72 2.28 2.8 1.56 1.4 2.86 1.84 3.26 2.04.4.2.64.16.88-.08.24-.24 1-1.16 1.26-1.56.24-.4.52-.32.88-.2.36.12 2.28 1.08 2.68 1.28.4.2.68.28.8.44.12.16.12.92-.2 1.82z" />
                    </svg>
                    {data.myAccount.inactive.buttonLabel}
                </a>
                <p className="mt-4 text-sm text-gray-500">
                    {data.myAccount.inactive.info} <br />
                    <p className="font-mono text-gray-700">join {activationCode}</p>
                    <span className="text-sm text-gray-500">{data.myAccount.inactive.info2}</span>
                </p>
            </div>
        );
    }

    const MyAccountPanel = () => {
        return (
            <div className="w-full h-full text-center flex flex-col items-center justify-around">
                <div className="flex flex-col items-center text-center">
                    <img
                        src={photoURL || `https://i.pravatar.cc/150`} // Random avatar based on email
                        alt="Profile"
                        className="w-24 h-24 rounded-full object-cover mb-4"
                    />
                    <h2 className="text-xl font-semibold">{displayName}</h2>
                    <p className="text-gray-500 capitalize mt-2">
                        {accountType || "Free"} Account
                    </p>
                    <div className="mt-4 w-full text-left">
                        <div>
                            <strong>IM Type:</strong> {imType}
                        </div>
                        <div>
                            <strong>Language:</strong> {language}
                        </div>
                        <div>
                            <strong>Activated At:</strong> {activatedAt? activatedAt.toDate().toLocaleDateString():" Not activated yet"}
                        </div>
                        <div>
                            <strong>IM Status:</strong>{" "}
                            <span
                                className={
                                    imState === "active"
                                        ? "text-green-600 font-medium"
                                        : "text-red-600 font-medium"
                                }
                            >
                                {imState}
                            </span>
                        </div>
                    </div>
                    <div className="space-y-4 mt-8">
                        <button
                            className="w-full bg-red-600 text-white font-semibold py-3 rounded-xl hover:bg-red-700 transition"
                            onClick={() => {
                                if (window.confirm("Are you sure you want to DELETE your account? This action is irreversible.")) {
                                    handleDeleteAccount();
                                }
                            }}
                        >
                            DELETE Account
                        </button>
                        <button
                            className="w-full bg-gray-800 text-white font-semibold py-3 rounded-xl hover:bg-gray-900 transition"
                            onClick={() => {
                                if (window.confirm("Are you sure you want to log out?")) {
                                    handleLogout();
                                }
                            }}
                        >
                            LOGOUT
                        </button>
                        <button
                            className="w-full bg-yellow-500 text-black font-semibold py-3 rounded-xl hover:bg-yellow-600 transition"
                            onClick={() => {
                                if (window.confirm("Are you sure you want to deactivate WhatsApp notifications?")) {
                                    handleDeactivateWhatsApp();
                                }
                            }}
                        >
                            Deactivate WhatsApp
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const handleLogout = async () => {
        await logout(); // Assuming this is from your AuthContext
        alert("Logged out successfully.");
        // Optionally clear any local storage or state
        // localStorage.clear();
        // Redirect to login or home page
        navigate("/"); // or your logout redirect
    };

    const handleDeleteAccount = async () => {
        try {
            await deleteUser(auth.currentUser.uid); // Firebase delete (if using Firebase)
            const userRef = doc(db, "users", user.uid); // if using Firestore
            await deleteDoc(userRef); // Firestore delete
            alert("Account deleted successfully.");
            navigate("/"); // Redirect to home or login page
            navigate("/signup");
        } catch (err) {
            console.error("Failed to delete account:", err);
        }
    };

    const handleDeactivateWhatsApp = async () => {
        const generateRandomCode = () => Math.floor(100000 + Math.random() * 900000).toString();

        try {
            const userRef = doc(db, "users", user.uid); // if using Firestore
            await updateDoc(userRef, {
                activationCode: generateRandomCode(), // Generate a new activation code
                imState: "inactive",
                imId: null,
            }); // Update the user's WhatsApp state in Firestore
            alert("WhatsApp has been deactivated.");
            navigate("/my-account"); // Redirect to the same page to refresh the state
        } catch (err) {
            console.error("Failed to deactivate WhatsApp:", err);
        }
    };


    return (
        <>
            <Navbar page="my-account" />
            <section className="w-full bg-pf-bgDarker relative h-[100vh] flex flex-col items-center justify-center">
                <div className="bg-white h-[90vh] max-h-[80vh] md:max-h-[700px] md:rounded-3xl rounded-lg shadow-2xl wrapper max-w-md flex flex-col justify-center items-center p-6">
                    {
                        imState === "inactive" ? <Activate /> : <MyAccountPanel />
                    }
                </div>
            </section>
        </>
    );
};

export default MyAccount;
